import { useState, useEffect, useRef, useCallback } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import axios from "../../utils/axiosDash";

// ── Thresholds ─────────────────────────────────────────────────────────────────
const getStatus = (n) => (n === 0 ? "empty" : n < 5 ? "moderate" : "crowded");

const STATUS = {
  empty:    { label: "Clear",    textCls: "text-emerald-600", bgCls: "bg-emerald-50",  ringCls: "ring-emerald-200",  dotCls: "bg-emerald-500",  barColor: "#10b981" },
  moderate: { label: "Moderate", textCls: "text-amber-600",   bgCls: "bg-amber-50",    ringCls: "ring-amber-200",    dotCls: "bg-amber-400",    barColor: "#f59e0b" },
  crowded:  { label: "Crowded",  textCls: "text-rose-600",    bgCls: "bg-rose-50",     ringCls: "ring-rose-200",     dotCls: "bg-rose-500",     barColor: "#f43f5e" },
};

const SERIES = [
  { key: "Total",   label: "Total",   color: "#60a5fa" },
  { key: "Kitchen", label: "Kitchen", color: "#a78bfa" },
  { key: "Sofa 1",  label: "Sofa 1",  color: "#fb923c" },
  { key: "Sofa 2",  label: "Sofa 2",  color: "#34d399" },
  { key: "Sofa 3",  label: "Sofa 3",  color: "#f472b6" },
];

const ZONES = [
  { key: "total_persons",   label: "Total Person In Frame",   icon: "👥", color: "#60a5fa", bg: "bg-blue-50",   ring: "ring-blue-200",   text: "text-blue-600"   },
  { key: "kitchen_persons", label: "Kitchen", icon: "🍳", color: "#a78bfa", bg: "bg-violet-50", ring: "ring-violet-200", text: "text-violet-600" },
  { key: "sofa1_persons",   label: "Sofa 1",  icon: "🛋️", color: "#fb923c", bg: "bg-orange-50", ring: "ring-orange-200", text: "text-orange-600" },
  { key: "sofa2_persons",   label: "Sofa 2",  icon: "🛋️", color: "#34d399", bg: "bg-emerald-50",ring: "ring-emerald-200",text: "text-emerald-600"},
  { key: "sofa3_persons",   label: "Sofa 3",  icon: "🛋️", color: "#f472b6", bg: "bg-pink-50",   ring: "ring-pink-200",   text: "text-pink-600"   },
];

// Donut color based on total count threshold
// Capacity = 6 seats
// green  : remaining > 3  (plenty of space)
// yellow : remaining 1-3  (filling up)
// red    : total > 6      (over capacity)
const CAPACITY = 6;
const getDonutConfig = (total) => {
  const remaining = CAPACITY - total;
  if (total > CAPACITY) return { color: "#f43f5e", trackColor: "#ffe4e6", label: "No Space",   sublabel: "No seats available",                                       labelCls: "text-rose-600",    bgCls: "bg-rose-50",    ringCls: "ring-rose-200"    };
  if (remaining <= 3)   return { color: "#f59e0b", trackColor: "#fef3c7", label: "Filling Up", sublabel: remaining + " seat" + (remaining === 1 ? "" : "s") + " left", labelCls: "text-amber-600",   bgCls: "bg-amber-50",   ringCls: "ring-amber-200"   };
  return                       { color: "#10b981", trackColor: "#d1fae5", label: "Available",  sublabel: remaining + " seat" + (remaining === 1 ? "" : "s") + " free", labelCls: "text-emerald-600", bgCls: "bg-emerald-50", ringCls: "ring-emerald-200" };
};

const TIME_FILTERS = [
  { label: "3 min",  value: 3   },
  { label: "5 min",  value: 5   },
  { label: "10 min", value: 10  },
  { label: "20 min", value: 20  },
  { label: "30 min", value: 30  },
  { label: "1 hr",   value: 60  },
  { label: "2 hr",   value: 120 },
];

// ── Helpers ────────────────────────────────────────────────────────────────────
const toIST = (raw) => {
  const ts = new Date(raw?.$date || raw);
  // subtract 330 min to convert stored UTC+0 → display IST
  const display = new Date(ts.getTime() - 330 * 60 * 1000);
  return display;
};

const fmtTime = (d) =>
  d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });

// ── Tooltips ───────────────────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white/95 backdrop-blur-sm border border-slate-200 rounded-xl shadow-lg p-3 text-xs min-w-[150px]">
      <p className="font-mono text-slate-400 mb-2 text-[11px] border-b border-slate-100 pb-1">{label}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 mb-1.5 last:mb-0">
          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: p.color }} />
          <span className="text-slate-500 font-medium">{p.name}</span>
          <span className="ml-auto font-bold text-slate-600">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

// ── Custom Donut Center Label ──────────────────────────────────────────────────
const DonutCenterLabel = ({ cx, cy, total, color, label, sublabel }) => (
  <>
    <text x={cx} y={cy - 14} textAnchor="middle" fill={color} fontSize={30} fontWeight={700} fontFamily="JetBrains Mono, monospace">
      {total}/{6}
    </text>
    <text x={cx} y={cy + 8} textAnchor="middle" fill="#94a3b8" fontSize={11} fontWeight={500}>
      occupied
    </text>
    <text x={cx} y={cy + 26} textAnchor="middle" fill={color} fontSize={10} fontWeight={700} letterSpacing={0.5}>
      {sublabel}
    </text>
  </>
);

// ── Spinner ────────────────────────────────────────────────────────────────────
const Spinner = ({ size = 8 }) => (
  <div className={`w-${size} h-${size} border-2 border-slate-200 border-t-blue-400 rounded-full animate-spin`} />
);

// ── Main Component ─────────────────────────────────────────────────────────────
export default function PersonCountDashboard() {
  // Live (cards + donut) — polls every 10s
  const [liveData,       setLiveData]       = useState(null);
  const [liveLoading,    setLiveLoading]    = useState(true);
  const [liveError,      setLiveError]      = useState(null);
  const [countdown,      setCountdown]      = useState(10);
  const [lastUpdated,    setLastUpdated]    = useState(null);

  // Historical (chart + table) — fetched on demand / filter change
  const [chartData,      setChartData]      = useState([]);
  const [chartLoading,   setChartLoading]   = useState(true);
  const [chartError,     setChartError]     = useState(null);
  const [timeFilter,     setTimeFilter]     = useState(3); // minutes — default 3 min

  const liveIntervalRef  = useRef(null);
  const countdownRef     = useRef(null);

  // ── Fetch live data ──────────────────────────────────────────────────────────
  const fetchLive = useCallback(async () => {
    try {
      const res = await axios.post("/fetch-tablespace-counts-live");
      setLiveData(res?.data?.data || null);
      setLastUpdated(new Date());
      setLiveError(null);
    } catch (err) {
      console.error("Live fetch error:", err);
      setLiveError("Unable to fetch live occupancy. Retrying...");
    } finally {
      setLiveLoading(false);
      setCountdown(10);
    }
  }, []);

  // ── Fetch historical data ────────────────────────────────────────────────────
  const fetchHistorical = useCallback(async (minutes) => {
    try {
      setChartLoading(true);
      setChartError(null);
      const res = await axios.post("/fetch-tablespace-counts", { minutes });
      const raw = res?.data?.data || [];

      const formatted = raw.map((r) => {
        const displayTs = toIST(r.timestamp);
        const kitchen = r.kitchen_persons || 0;
        const sofa1 = r.sofa1_persons || 0;
        const sofa2 = r.sofa2_persons || 0;
        const sofa3 = r.sofa3_persons || 0;

        return {
          time: fmtTime(displayTs),
          Total: kitchen + sofa1 + sofa2 + sofa3,
          Kitchen: kitchen,
          "Sofa 1": sofa1,
          "Sofa 2": sofa2,
          "Sofa 3": sofa3,
        };
      });

      setChartData(formatted);
    } catch (err) {
      console.error("Historical fetch error:", err);
      setChartError("Unable to fetch historical data.");
    } finally {
      setChartLoading(false);
    }
  }, []);

  // ── Boot ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchLive();
    fetchHistorical(3);

    liveIntervalRef.current = setInterval(fetchLive, 10000);
    countdownRef.current    = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);

    return () => {
      clearInterval(liveIntervalRef.current);
      clearInterval(countdownRef.current);
    };
  }, [fetchLive, fetchHistorical]);

  // ── Time filter change ────────────────────────────────────────────────────────
  const handleFilterChange = (minutes) => {
    setTimeFilter(minutes);
    fetchHistorical(minutes);
  };

  // ── Donut data ────────────────────────────────────────────────────────────────
  const donutTotal = liveData?.total_persons || 0;
  const donutCfg   = getDonutConfig(donutTotal);
  // Ring is always relative to CAPACITY (6)
  // Over capacity → full red circle
  // 0 people      → full green circle
  // otherwise     → filled arc (occupied) + remaining arc
  const occupied   = Math.min(donutTotal, CAPACITY);
  const remaining  = Math.max(0, CAPACITY - donutTotal);
  const donutData  = donutTotal === 0
    ? [{ name: "All Free", value: 1, color: donutCfg.color }]
    : donutTotal >= CAPACITY
      ? [{ name: "Over Capacity", value: 1, color: donutCfg.color }]
      : [
          { name: "Occupied", value: occupied,  color: donutCfg.color       },
          { name: "Free",     value: remaining, color: donutCfg.trackColor  },
        ];

  // ── Countdown ring ────────────────────────────────────────────────────────────
  const circumference = 2 * Math.PI * 16;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white font-sans">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,300;14..32,400;14..32,500;14..32,600;14..32,700&family=JetBrains+Mono:wght@400;500&display=swap');
        * { font-family: 'Inter', sans-serif; }
        .mono { font-family: 'JetBrains Mono', monospace; }
      `}</style>

      {/* ── Top Bar ─────────────────────────────────────────────────────────── */}
      <div className="border-b border-slate-200/80 bg-white/90 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-400 to-blue-500 flex items-center justify-center text-white text-sm font-bold shadow-sm">
              P
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-700 leading-tight">Person Count Monitor</p>
              <p className="text-[11px] text-slate-400 mono">
                {lastUpdated ? `Last sync: ${lastUpdated.toLocaleTimeString()}` : "Connecting..."}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-full px-4 py-1.5 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-xs font-semibold text-emerald-700 tracking-wide">LIVE STREAM</span>
            </div>

            <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-2xl px-4 py-2 shadow-sm">
              <div className="relative w-8 h-8">
                <svg className="w-8 h-8 -rotate-90">
                  <circle cx="16" cy="16" r="14" fill="none" stroke="#e2e8f0" strokeWidth="2.5" />
                  <circle
                    cx="16" cy="16" r="14" fill="none" stroke="#93c5fd" strokeWidth="2.5"
                    strokeDasharray={circumference}
                    strokeDashoffset={circumference * (1 - countdown / 10)}
                    strokeLinecap="round"
                    style={{ transition: "stroke-dashoffset 1s linear" }}
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-[10px] font-mono font-bold text-slate-500">{countdown}</span>
              </div>
              <div>
                <p className="text-[9px] text-slate-400 font-semibold uppercase tracking-wider leading-tight">Refresh</p>
                <p className="text-xs font-semibold text-slate-500 mono leading-tight">every 10s</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">

        {liveError && (
          <div className="flex items-center gap-3 bg-rose-50/80 border border-rose-200 text-rose-500 text-sm rounded-2xl px-5 py-3.5 shadow-sm">
            <span className="text-base">⚠️</span>
            <span className="font-medium">{liveError}</span>
          </div>
        )}

        {/* ── Section 1: Live Occupancy Cards + Donut ─────────────────────── */}
        {liveLoading ? (
          <div className="flex flex-col items-center justify-center py-32 gap-4">
            <Spinner size={10} />
            <p className="text-slate-400 text-sm font-medium">Loading live occupancy...</p>
          </div>
        ) : liveData && (
          <div>
            {/* Section header */}
            <div className="flex items-center gap-2 mb-4">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Current Occupancy</h2>
              {lastUpdated && (
                <span className="ml-auto text-[11px] text-slate-400 mono">
                  as of {fmtTime(lastUpdated)}
                </span>
              )}
            </div>

            {/* Cards + Donut side-by-side */}
            <div className="flex flex-col lg:flex-row gap-6">

              {/* Zone cards — 5 in a wrapped grid */}
              <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4 content-start">
                {ZONES.map((zone) => {
                  const count = liveData[zone.key] || 0;
                  const st    = STATUS[getStatus(count)];
                  return (
                    <div
                      key={zone.key}
                      className="relative bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col gap-3 overflow-hidden hover:shadow-md transition-shadow"
                    >
                      <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl" style={{ background: zone.color }} />
                      <div className="flex items-center justify-between">
                        <span className="text-xl">{zone.icon}</span>
                        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${st.bgCls} ${st.textCls} ring-1 ${st.ringCls}`}>
                          {st.label}
                        </span>
                      </div>
                      <div>
                        <p className="text-3xl font-bold mono" style={{ color: zone.color }}>{count}</p>
                        <p className="text-xs text-slate-400 font-medium mt-0.5">{zone.label}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Donut chart */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-col min-w-[280px] lg:w-[320px]">
                <div className="mb-1">
                  <h3 className="text-sm font-semibold text-slate-600">Zone Distribution</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Live occupancy status</p>
                </div>

                <div className="flex-1 flex flex-col items-center justify-center gap-4">
                  <PieChart width={220} height={200}>
                    <Pie
                      data={donutData}
                      cx={110}
                      cy={95}
                      innerRadius={62}
                      outerRadius={90}
                      startAngle={90}
                      endAngle={-270}
                      paddingAngle={donutTotal === 0 || donutTotal >= CAPACITY ? 0 : 2}
                      dataKey="value"
                      stroke="none"
                    >
                      {donutData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                      <DonutCenterLabel cx={110} cy={95} total={donutTotal} color={donutCfg.color} label={donutCfg.label} sublabel={donutCfg.sublabel} />
                    </Pie>
                  </PieChart>

                  {/* Status badge */}
                  <div className="flex flex-col items-center gap-2">
                    <span className={`text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full ${donutCfg.bgCls} ${donutCfg.labelCls} ring-1 ${donutCfg.ringCls}`}>
                      {donutTotal === 0 ? "🟢 Available" : donutTotal > CAPACITY ? "🔴 No Space" : "🟡 Filling Up"}
                    </span>
                    {donutTotal > CAPACITY && (
                      <p className="text-xs text-rose-500 font-semibold text-center">
                        ⚠️ Over capacity by {donutTotal - CAPACITY} {donutTotal - CAPACITY === 1 ? "person" : "people"}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Section 2: Occupancy Trends (historical) ──────────────────────── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 pb-2 border-b border-slate-100 bg-slate-50/30">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-xl font-semibold text-slate-700">Occupancy Trends</h2>
                <p className="text-sm text-slate-400 mt-1">
                  Last {TIME_FILTERS.find(f => f.value === timeFilter)?.label} · {chartData.length} snapshots
                  {chartData.length === 0 && !chartLoading && (
                    <span className="text-amber-400 ml-2">— no data in this window</span>
                  )}
                </p>
              </div>

              {/* Time filter pills */}
              <div className="flex flex-wrap gap-2">
                {TIME_FILTERS.map((f) => (
                  <button
                    key={f.value}
                    onClick={() => handleFilterChange(f.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                      timeFilter === f.value
                        ? "bg-blue-500 text-white shadow-sm"
                        : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-5 mt-5 pb-1">
              {SERIES.map((s) => (
                <div key={s.key} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm" style={{ background: s.color }} />
                  <span className="text-xs text-slate-500 font-medium">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="p-4 pt-6">
            {chartError && (
              <div className="flex items-center gap-3 bg-rose-50 border border-rose-200 text-rose-500 text-sm rounded-xl px-4 py-3 mb-4">
                <span>⚠️</span><span>{chartError}</span>
              </div>
            )}

            {chartLoading ? (
              <div className="flex flex-col items-center justify-center h-64 gap-4">
                <Spinner size={10} />
                <p className="text-slate-400 text-sm font-medium">Fetching occupancy trends...</p>
              </div>
            ) : chartData.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 gap-3 text-slate-300">
                <span className="text-4xl">📭</span>
                <p className="text-sm font-medium">No data in this time window</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={340}>
                <AreaChart data={chartData} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="4 4" stroke="#e2e8f0" vertical={false} />
                  <XAxis
                    dataKey="time"
                    tick={{ fontSize: 10, fill: "#94a3b8", fontFamily: "JetBrains Mono" }}
                    tickLine={false}
                    axisLine={{ stroke: "#cbd5e1", strokeWidth: 1 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#94a3b8" }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                    label={{ value: "People Count", angle: -90, position: "insideLeft", fontSize: 11, fill: "#94a3b8", offset: -5 }}
                  />
                  <ReferenceLine
                    y={5}
                    stroke="#fbbf24"
                    strokeDasharray="5 5"
                    strokeWidth={1.5}
                    label={{ value: "⚠️ Crowded Threshold", position: "insideTopRight", fontSize: 10, fill: "#fbbf24", fontWeight: 600, offset: 5 }}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  {SERIES.map((s) => (
                    <Area
                      key={s.key}
                      type="monotone"
                      dataKey={s.key}
                      stroke={s.color}
                      strokeWidth={2}
                      fill="none"
                      fillOpacity={0}
                      dot={false}
                      activeDot={{ r: 5, stroke: "#ffffff", strokeWidth: 2.5, fill: s.color }}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* ── Section 3: Recent Snapshots Table (historical) ───────────────── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/30 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-lg font-semibold text-slate-700">Recent Snapshots</h2>
              <p className="text-sm text-slate-400 mt-0.5">
                Occupancy per zone — last {TIME_FILTERS.find(f => f.value === timeFilter)?.label}
              </p>
            </div>
          </div>

          <div className="overflow-x-auto p-1">
            {chartLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner size={8} />
              </div>
            ) : chartData.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3 text-slate-300">
                <span className="text-3xl">📭</span>
                <p className="text-sm font-medium">No snapshots in this window</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/50">
                    {["Time", "Total", "Kitchen", "Sofa 1", "Sofa 2", "Sofa 3"].map((h) => (
                      <th key={h} className="text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider py-4 px-5 first:pl-6 last:pr-6">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...chartData].reverse().slice(0, 20).map((row, i) => (
                    <tr key={i} className="group hover:bg-slate-50/80 transition-all duration-150 border-b border-slate-100 last:border-0">
                      <td className="py-3 px-5 first:pl-6 mono text-xs text-slate-400 font-medium">{row.time}</td>
                      {["Total", "Kitchen", "Sofa 1", "Sofa 2", "Sofa 3"].map((col) => {
                        const st = STATUS[getStatus(row[col])];
                        return (
                          <td key={col} className="py-3 px-5">
                            <span className={`inline-flex items-center justify-center mono text-sm font-semibold w-10 h-8 rounded-xl ${st.bgCls} ${st.textCls} ring-1 ${st.ringCls} ring-opacity-60`}>
                              {row[col]}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* ── Footer ────────────────────────────────────────────────────────── */}
        {liveData && (
          <div className="flex items-center justify-between text-xs text-slate-400 pt-2 pb-4 flex-wrap gap-3 border-t border-slate-100 mt-4">
            <div className="flex items-center gap-2">
              <span className="mono text-[11px] bg-slate-100 px-2 py-1 rounded-md text-slate-400">
                event_id: {liveData.event_id}
              </span>
              <span className="text-slate-300">•</span>
              <span className="text-slate-400">Live cards refresh every 10s</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-emerald-500 font-semibold tracking-wide text-[11px] uppercase">Active Monitoring</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}